import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  XMarkIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { sampleProducts, Product } from '../data/products';
import './SearchBar.css';

interface SearchBarProps {
  onClose?: () => void;
  isMobile?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({ onClose, isMobile = false }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [hasAnimated, setHasAnimated] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Trigger entry animation on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setHasAnimated(true);
    }, 100); // Small delay for better effect
    return () => clearTimeout(timer);
  }, []);

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Save recent searches to localStorage
  const saveRecentSearch = (searchTerm: string) => {
    const updated = [searchTerm, ...recentSearches.filter(term => term !== searchTerm)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };

  // Search products
  const searchProducts = (searchQuery: string) => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const results = sampleProducts.filter(product =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.category.toLowerCase().includes(searchQuery.toLowerCase())
    ).slice(0, 8);

    setSearchResults(results);
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);
    
    if (value.trim().length > 0) {
      setIsOpen(true);
      searchProducts(value);
    } else {
      setIsOpen(false);
      setSearchResults([]);
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      saveRecentSearch(query.trim());
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      setIsOpen(false);
      if (onClose) onClose();
    }
  };

  // Handle result click
  const handleResultClick = (product: Product) => {
    saveRecentSearch(product.name);
    navigate(`/product/${product.id}`);
    setIsOpen(false);
    if (onClose) onClose();
  };

  // Handle recent search click
  const handleRecentSearchClick = (searchTerm: string) => {
    setQuery(searchTerm);
    navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    setIsOpen(false);
    if (onClose) onClose();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    const totalItems = searchResults.length + recentSearches.length;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < totalItems - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          if (selectedIndex < searchResults.length) {
            handleResultClick(searchResults[selectedIndex]);
          } else {
            const recentIndex = selectedIndex - searchResults.length;
            handleRecentSearchClick(recentSearches[recentIndex]);
          }
        } else if (query.trim()) {
          handleSubmit(e);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Clear search
  const clearSearch = () => {
    setQuery('');
    setIsOpen(false);
    setSearchResults([]);
    setSelectedIndex(-1);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`search-bar ${isMobile ? 'mobile' : ''} ${hasAnimated ? 'animated' : ''}`} ref={searchRef}>
      <form onSubmit={handleSubmit} className="search-form">
        <div className="search-input-container">
          <input
            ref={inputRef}
            type="text"
            placeholder={t('searchBar.placeholder')}
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => query.trim().length > 0 && setIsOpen(true)}
            className="search-input"
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              onClick={clearSearch}
              className="clear-button"
              aria-label={t('searchBar.clearSearch')}
            >
              <XMarkIcon className="clear-icon" />
            </button>
          )}
          <button type="submit" className="search-button">
            <MagnifyingGlassIcon className="search-icon" />
          </button>
        </div>
      </form>

      {/* Search Dropdown */}
      {isOpen && (
        <div className="search-dropdown">
          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="search-section">
              <h4 className="section-title">{t('searchBar.products')}</h4>
              <div className="search-results">
                {searchResults.map((product, index) => (
                  <div
                    key={product.id}
                    className={`search-result ${selectedIndex === index ? 'selected' : ''}`}
                    onClick={() => handleResultClick(product)}
                  >
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="result-image"
                    />
                    <div className="result-details">
                      <h5 className="result-name">{product.name}</h5>
                      <p className="result-brand">{product.brand}</p>
                      <div className="result-price">
                        <span className="current-price">₪{product.price.toFixed(2)}</span>
                        {product.originalPrice && (
                          <span className="original-price">₪{product.originalPrice.toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Searches */}
          {recentSearches.length > 0 && searchResults.length === 0 && (
            <div className="search-section">
              <h4 className="section-title">
                <ClockIcon className="section-icon" />
                {t('searchBar.recentSearches')}
              </h4>
              <div className="recent-searches">
                {recentSearches.map((searchTerm, index) => (
                  <button
                    key={searchTerm}
                    className={`recent-search ${selectedIndex === searchResults.length + index ? 'selected' : ''}`}
                    onClick={() => handleRecentSearchClick(searchTerm)}
                  >
                    <ClockIcon className="recent-icon" />
                    <span>{searchTerm}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* No Results */}
          {searchResults.length === 0 && recentSearches.length === 0 && query.trim().length >= 2 && (
            <div className="no-results">
              <p>{t('searchBar.noProductsFoundFor', { query })}</p>
              <button 
                className="search-all-button"
                onClick={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
              >
                {t('searchBar.searchAllProducts')}
              </button>
            </div>
          )}

          {/* Quick Actions */}
          <div className="quick-actions">
            <button 
              className="quick-action"
              onClick={() => navigate('/vitamins')}
            >
              {t('searchBar.browseVitamins')}
            </button>
            <button 
              className="quick-action"
              onClick={() => navigate('/supplements')}
            >
              {t('searchBar.browseSupplements')}
            </button>
            <button 
              className="quick-action"
              onClick={() => navigate('/beauty')}
            >
              {t('searchBar.browseBeauty')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
